import React, { useState } from 'react';
import './ForgotPassword.css';
import { ResetPasswordCall } from './Requests';
import { PulseLoader } from 'react-spinners';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ResetPassword = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();

  const queryParameters = new URLSearchParams(window.location.search);
  const tokenFromUrlParam = queryParameters.get('token');

  const [token] = useState(tokenFromUrlParam);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [status, setStatus] = useState('reset'); // 'reset' | 'success' | 'error'
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRetype, setShowRetype] = useState(false);

  function updateCanSubmit(p1, p2) {
    const a = (p1 ?? '').trim();
    const b = (p2 ?? '').trim();
    setCanSubmit(!!a && !!b);
  }

  function onNewPasswordChange(e) {
    const v = e.target.value;
    setNewPassword(v);
    updateCanSubmit(v, confirmPassword);
  }

  function onConfirmPasswordChange(e) {
    const v = e.target.value;
    setConfirmPassword(v);
    updateCanSubmit(newPassword, v);
  }

  function onSubmit(e) {
    e.preventDefault();
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError(t('reset.validation.passwordMismatch'));
      return;
    }
    if ((newPassword || '').length < 8) {
      setPasswordError(t('reset.validation.passwordMin'));
      return;
    }

    setIsLoading(true);
    ResetPasswordCall(newPassword, token)
      .then((res) => {
        if (res.status === 200) setStatus('success');
        else setStatus('error');
      })
      .catch(() => setStatus('error'))
      .finally(() => setIsLoading(false));
  }

  if (token == null) {
    // No token → show nothing (or you could render an error screen with its own key)
    return <div />;
  }

  return (
    <div className="login-background">
      <div className="login-container">
        <div style={{ justifyContent: 'center', display: 'flex' }}>
          <PulseLoader
            color="#333"
            loading={isLoading}
            size={25}
            aria-label={t('reset.loadingAria')}
            data-testid="loader"
          />
        </div>

        {status === 'reset' && !isLoading ? (
          <div>
            <h1 className="login-title">{t('reset.title')}</h1>

            <form className="login-form" onSubmit={onSubmit}>
              <p className="login-subtitle" style={{ marginBottom: '0.5rem', marginTop: '1rem' }}>
                {t('reset.password.label')}
              </p>
              <div className="password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder={t('reset.password.placeholder')}
                  onChange={onNewPasswordChange}
                />
                <button
                  type="button"
                  className="eye-button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={t('reset.password.toggleAria')}
                >
                  {showPassword ? <FaEyeSlash className="eye-icon" /> : <FaEye className="eye-icon" />}
                </button>
              </div>

              <p className="login-subtitle" style={{ marginBottom: '0.5rem', marginTop: '1rem' }}>
                {t('reset.confirm.label')}
              </p>
              <div className="password-wrapper">
                <input
                  type={showRetype ? 'text' : 'password'}
                  id="retype-password"
                  placeholder={t('reset.confirm.placeholder')}
                  onChange={onConfirmPasswordChange}
                />
                <button
                  type="button"
                  className="eye-button"
                  onClick={() => setShowRetype(!showRetype)}
                  aria-label={t('reset.confirm.toggleAria')}
                >
                  {showRetype ? <FaEyeSlash className="eye-icon" /> : <FaEye className="eye-icon" />}
                </button>
              </div>

              {passwordError && <p style={{ color: 'red' }}>{passwordError}</p>}

              <button className="signin-btn" disabled={!canSubmit}>
                {t('reset.actions.submit')}
              </button>
            </form>
          </div>
        ) : status === 'error' && !isLoading ? (
          <div style={{ textAlign: 'center' }}>
            <h1>{t('reset.error.title')}</h1>
            <p>{t('reset.error.body')}</p>
          </div>
        ) : status === 'success' && !isLoading ? (
          <div className="login-form" style={{ textAlign: 'center', width: '100%' }}>
            <h1>{t('reset.success.title')}</h1>
            <p className="mt-2">{t('reset.success.body')}</p>
            <button
              style={{ justifyContent: 'center' }}
              onClick={() => navigate('/login')}
              className="signin-btn"
            >
              {t('reset.actions.goToLogin')}
            </button>
          </div>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
